import { Att } from '@cumlaude/metadata';
import { InstellingBron, RCumLaudeModule, RRol } from '@cumlaude/service-contract';
import { DashboardOptionDefinition, dashboardOptionsFor } from './dashboard-options';

/**
 * Overschrijft voor één attribuut de filter/grouping/exportable uit de metadata-yaml. Wat niet wordt
 * genoemd blijft staan zoals de yaml het heeft.
 */
export type AttOverride = {
	readonly filter?: boolean;
	readonly grouping?: boolean;
	readonly exportable?: boolean;
};

/**
 * Object met Att als key om de filter/grouping/exportable te overriden per dashboard.
 * De key mag een fk zijn om de hele join te overriden.
 */
export type AttOverrides = { readonly [att in Att]?: AttOverride };

const NIET_BESCHIKBAAR: AttOverride = { filter: false, grouping: false, exportable: false };
const BESCHIKBAAR: AttOverride = { filter: true, grouping: true, exportable: true };

/** Bron van een instelling, zoals de opties per bron van een custom filter die kennen. */
export type CustomFilterBron = 'somtoday' | 'magister';

export type CustomFilterOptie = string | number | boolean;

/**
 * Een custom filter (`x_`-naam) van een dashboard: een filter zonder eigen metadata-veld erachter,
 * dat de AI-chat dus niet uit het domeinschema kent. `generate-dashboard-manifest.ts` zet het in het
 * manifest en de systeemprompt, zodat het model het kan instellen. De waarden zijn die van de
 * filterconfig in `filter-config.ts`; `FilterPanelComponent` controleert in dev-mode of ze nog
 * kloppen.
 */
export type CustomFilter = {
	/** De filternaam, bijv. `x_peildatum`. */
	readonly att: string;
	/** Het label in het filterpaneel. */
	readonly label: string;
	/** De waarden die het filter accepteert. Weglaten bij een vrije of datagestuurde keuze. */
	readonly opties?: readonly CustomFilterOptie[];
	/** Opties voor een bepaalde bron; die gaan voor op `opties`. */
	readonly optiesPerBron?: { readonly [bron in CustomFilterBron]?: readonly CustomFilterOptie[] };
	/** De waarde die het filter heeft als de gebruiker er niets aan doet. */
	readonly default?: CustomFilterOptie;
	/** AI-gerichte uitleg: wat het filter doet en wanneer het model het moet zetten. */
	readonly omschrijving: string;
	/** De variant waarin het filter bestaat. Weglaten betekent beide. */
	readonly variant?: 'actueel' | 'historie';
};

/**
 * De opties van `x_peildatum` en `x_vakkeuze_peildatum`.
 */
export const PEILDATUM_OPTIES = [
	'Actueel',
	'1 augustus',
	'1 september',
	'1 oktober',
	'1 november',
	'1 december',
	'1 januari',
	'1 februari',
	'1 maart',
	'1 april',
	'1 mei',
	'1 juni',
	'1 juli',
] as const;

/**
 * De opties van `x_advies_leerlinggroep`.
 */
export const ADVIES_LEERLINGGROEP_OPTIES = ['Instromers', 'Examenkandidaten'] as const;

/**
 * Alle cijfertypes van `x_cijfertype`, in de volgorde van de metadata-enum `Cijferkolomtype`.
 */
export const CIJFERTYPE_OPTIES = [
	'Toets',
	'Advies',
	'Laatste periodegemiddelde',
	'Laatste gemiddelde',
	'Laatste rapportcijfer',
	'Periodegemiddelde',
	'Gemiddelde',
	'Rapportcijfer',
	'SE-cijfer',
	'CE-cijfer',
	'Eindcijfer',
] as const;

/**
 * De cijfertypes die bij een bron beschikbaar zijn, voor `x_cijfertype`. Dit is ook de bron van
 * `cijferKolomTypeAllowed` in `filter-config.ts`.
 * - Somtoday: alles behalve Advies; dat is alleen te zien op Cijfers > Toets en Details > Leerling > Cijferlijst.
 * - Magister: geen (Laatste) Rapportcijfer, (Laatste) Periodegemiddelde en Advies; die zijn daar niet beschikbaar.
 */
export const CIJFERTYPE_OPTIES_PER_BRON = {
	somtoday: CIJFERTYPE_OPTIES.filter((optie) => optie !== 'Advies'),
	magister: ['Toets', 'Laatste gemiddelde', 'Gemiddelde', 'SE-cijfer', 'CE-cijfer', 'Eindcijfer'],
} as const satisfies { readonly [bron in CustomFilterBron]: readonly (typeof CIJFERTYPE_OPTIES)[number][] };

/** De opties van `x_cijferlijst_cijfertype`: dat filter kent Toets en Advies niet. */
const CIJFERLIJST_CIJFERTYPE_OPTIES = CIJFERTYPE_OPTIES.filter((optie) => optie !== 'Toets' && optie !== 'Advies');

const CIJFERLIJST_CIJFERTYPE_OPTIES_PER_BRON = {
	somtoday: CIJFERLIJST_CIJFERTYPE_OPTIES,
	magister: CIJFERTYPE_OPTIES_PER_BRON.magister.filter((optie) => optie !== 'Toets'),
} as const;

/** De lijnen van `x_abs_geoorloofd` (Afwezigheid, Historie). */
export const ABSENTIE_LIJNEN_OPTIES = ['Afwezigheid totaal', 'Geoorloofde afwezigheid', 'Ongeoorloofde afwezigheid'] as const;

const PEILDATUM_CUSTOM_FILTER = {
	att: 'x_peildatum',
	label: 'Peildatum',
	opties: PEILDATUM_OPTIES,
	default: '1 oktober',
	omschrijving:
		'Welke peildatum de plaatsing geldt. Zonder dit filter geldt 1 oktober, ook als de gebruiker niets noemt. Zet `Actueel` (de meest recente plaatsing, ook na een wijziging na 1 oktober, zoals een leerling die later naar een andere opleiding gaat) zodra de gebruiker de huidige of actuele situatie wil. Filter nooit op `ds_fun_is_plaatsing_peildatum_*`; kies hier de datum.',
} as const satisfies CustomFilter;

const CIJFERTYPE_CUSTOM_FILTER = {
	att: 'x_cijfertype',
	label: 'Cijfertype',
	optiesPerBron: CIJFERTYPE_OPTIES_PER_BRON,
	default: 'Laatste gemiddelde',
	omschrijving:
		'Welk soort cijfer wordt getoond. Altijd gezet, standaard Laatste gemiddelde. Zet het zodra de gebruiker een ander cijfer bedoelt, zoals SE-cijfer, CE-cijfer, Eindcijfer, Rapportcijfer of Periodegemiddelde.',
} as const satisfies CustomFilter;

const PERIODE_OMSCHRIJVING =
	'De periode van het cijfer, als de periodecode zoals die in de data van de school staat (vrije tekst, bijvoorbeeld P1 of R1; het formaat verschilt per school en bron). Dit filter werkt alleen bij cijfertype Periodegemiddelde, Gemiddelde of Rapportcijfer en wordt dan automatisch op de eerste periode van de school gezet. Zet het alleen als de gebruiker een periode noemt, met de exacte code zoals die in de data staat, en filter dan niet ook zelf op `cf_fun_nm_periode`; dat geeft twee periodefilters tegelijk. Bij elk ander cijfertype doet dit filter niets: filter een periode dan wel op het gewone veld `cf_fun_nm_periode`.';

const GEM_PERIODE_CUSTOM_FILTER = {
	att: 'x_gem_periode',
	label: 'Periode',
	omschrijving: PERIODE_OMSCHRIJVING,
} as const satisfies CustomFilter;

const CIJFER_METZONDER_CE_CUSTOM_FILTER = {
	att: 'x_cijfer_metzonder_ce',
	label: 'Toon vakken',
	opties: [true, false],
	default: true,
	omschrijving:
		'`true` toont alleen vakken met een CE-cijfer, `false` alle vakken met een SE- en/of CE-cijfer. Zet `false` als de gebruiker ook vakken zonder centraal examen wil zien.',
} as const satisfies CustomFilter;

/**
 * Eén node in de dashboardboom. Bladknopen (geen `children`) zijn echte dashboards;
 * tussenknopen zijn navigatiegroepen die zelf geen component renderen.
 *
 * Geen Angular-imports — dit bestand is ook bruikbaar buiten de Angular-context
 * (admin-app, buildscripts, backend-manifest).
 */
export type RegistryNode = {
	readonly path: string;
	readonly name?: string;
	readonly title?: string;
	readonly base?: string;
	readonly top?: boolean;
	readonly root?: boolean;
	readonly tab?: string;
	readonly vo?: boolean;
	readonly vso?: boolean;
	readonly requiredRollen?: readonly RRol[];
	readonly requiredModule?: RCumLaudeModule;
	readonly requiredBron?: InstellingBron;
	readonly requiresSupport?: boolean;
	readonly fixedWeergaveOpties?: readonly string[];
	readonly flexibleWeergaveOpties?: readonly string[];
	/**
	 * Globale opties uit `DASHBOARD_OPTIONS` die dit dashboard niet ondersteunt, zoals `sortOrder` zonder sorteerbare lijst.
	 */
	readonly unsupportedOptions?: readonly string[];
	readonly defaultGroeperingen?: readonly (readonly string[])[];
	readonly keepGroepering?: boolean;
	/**
	 * Geeft aan dat deze node zijn eigen (bestaande) tabbalk met subpagina's toont in plaats van dat
	 * de subpagina's in de verticale hoofdnavigatie worden uitgeklapt. Voor detailpagina's die je via
	 * een drill-down bereikt (bv. Details > Leerling), niet via het menu.
	 */
	readonly eigenTabbalk?: boolean;
	/**
	 * Wordt door BasisvaardigheidOverzichtComponent en LeerlingBasisvaardighedenDetailsPanelComponent
	 * via Angular's route data binding ingelezen als typed input. Altijd gelijk aan `name` voor
	 * basisvaardigheden-routes, maar een apart veld omdat de component input op deze naam bindt.
	 */
	readonly bv_nm_vaardigheid?: string;
	/** Velden die op dit dashboard anders beschikbaar zijn dan de metadata-yaml van het domein zegt. */
	readonly attOverrides?: AttOverrides;
	/** Technische domeinsleutel voor de AI-chat, bijv. 'fac_ds_doorstroom'. */
	readonly domain?: string;
	/**
	 * Het schooljaarfilter van dit dashboard, als dat afwijkt van het standaardveld van zijn domein.
	 * Alleen nodig voor de AI-chat: `generate-dashboard-manifest.ts` zet het in het manifest en de
	 * systeemprompt, zodat het model op hetzelfde veld filtert als het dashboard zelf gebruikt.
	 */
	readonly schooljaarFilter?: string;
	/**
	 * De custom filters (`x_`) die dit dashboard heeft, zonder het schooljaarfilter (dat staat bij
	 * `schooljaarFilter`). Alleen nodig voor de AI-chat; zie {@link CustomFilter}.
	 */
	readonly customFilters?: readonly CustomFilter[];
	/**
	 * Wat dit dashboard met groeperingen doet. Het dashboard leest het zelf uit de route-data
	 * (`DataTreeTableConfig`) en `generate-dashboard-manifest.ts` zet het in het manifest en de
	 * systeemprompt, zodat de AI-chat dezelfde grenzen kent als de gebruiker ziet. De atts staan hier
	 * als punt-notatie (`ds_fk_ll.ll_nm_svh`) en niet als segmenten zoals bij `defaultGroeperingen`,
	 * omdat het model ze zo teruggeeft.
	 *
	 * Weglaten betekent onbeperkt; dit vervangt `flexibleMaxGroups` in de component.
	 */
	readonly maxGroeperingen?: number;
	/**
	 * De enige groeperingen die dit dashboard aankan; vervangt `availableGroups` met
	 * `allowAllAvailableGroups: false` in de component. Zonder dit veld kan de gebruiker via "Alle
	 * groeperingen" elk veld van het domein kiezen, en mag het model dat dus ook voorstellen. De
	 * `availableGroups` van de overige dashboards blijven in de component staan: dat is een lijst
	 * veelgebruikte groeperingen en geen grens.
	 */
	readonly toegestaneGroeperingen?: readonly string[];
	/** Vrije tekst voor een beperking die geen lijst is, zoals bij Overgang (`filterAllGroups`). */
	readonly groeperingRegel?: string;
	/**
	 * Velden waarop het dashboard al uitsplitst; daar apart op groeperen voegt niets toe. Bewust een
	 * samenvatting voor het model en niet de bron van `subgroups` in de component: die bevat ook
	 * technische niveaus en wordt vaak per weergave berekend. `BaseDashboardComponent` controleert in
	 * dev-mode dat deze lijst geen velden claimt die het dashboard niet gebruikt.
	 */
	readonly subgroepen?: readonly string[];
	/** Groeperingen die het dashboard zelf toevoegt en die het model dus niet hoeft te geven. */
	readonly vasteGroeperingen?: readonly string[];
	/** AI-gerichte omschrijving van het dashboard (Nederlands). */
	readonly description?: string;
	/** Child dashboards van deze RegistryNode. */
	readonly children?: readonly RegistryNode[];
};

/**
 * Extraheert alle bladknoop-paden uit een registryboom als een union van volledige URL-strings.
 * Tussenknopen (nodes mét `children`) worden overgeslagen; alleen bladknopen leveren een pad op.
 *
 * @example
 *   type LeafPath = ExtractLeafPaths<typeof DASHBOARD_REGISTRY>;
 *   // → 'onderwijsresultaten/onderwijsresultaten' | 'cijfers/overzicht' | ...
 */
export type ExtractLeafPaths<Nodes extends ReadonlyArray<RegistryNode>, Prefix extends string = ''> = Nodes[number] extends infer Node
	? Node extends { path: infer P extends string; children: infer C extends ReadonlyArray<RegistryNode> }
		? ExtractLeafPaths<C, Prefix extends '' ? P : `${Prefix}/${P}`>
		: Node extends { path: infer P extends string }
			? Prefix extends ''
				? P
				: `${Prefix}/${P}`
			: never
	: never;

export const DASHBOARD_REGISTRY = [
	{
		path: 'onderwijsresultaten',
		name: 'Onderwijsresultaten',
		base: 'core',
		children: [
			{
				path: 'onderwijsresultaten',
				name: 'Onderwijsresultaten',
				title: 'Onderwijsresultaten %prognose%',
				vo: true,
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
				fixedWeergaveOpties: ['variant'],
				flexibleWeergaveOpties: ['indicator-over', 'toon-trend', 'inspectie-data'],
				domain: 'fac_or_onderwijsresultaten',
				description:
					'Toont de vier onderwijsindicatoren (Onderwijspositie, Onderbouwsnelheid, Bovenbouwsucces, Examencijfers) per vestiging of niveau, vergeleken met de gecorrigeerde norm van de onderwijsinspectie. Per indicator wordt met kleur (oranje/rood) aangegeven of de school onder de norm presteert, inclusief een historisch overzicht.',
			},
			{
				path: 'onderwijspositie',
				name: 'Onderwijspositie t.o.v. advies PO',
				title: 'Onderwijspositie t.o.v. advies PO %prognose%',
				vo: true,
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
				fixedWeergaveOpties: ['variant'],
				flexibleWeergaveOpties: ['indicator-over', 'or-uitsluiten'],
				defaultGroeperingen: [['ds_fk_ll', 'll_nm_basisschooladvies']],
				domain: 'fac_ds_doorstroom',
				maxGroeperingen: 1,
				toegestaneGroeperingen: ['ds_fun_nm_niveau_volgend_sj', 'ds_fk_ll.ll_nm_basisschooladvies', 'ds_fk_ll.ll_nm_svh'],
				subgroepen: ['ds_fun_co_verschil_basisschooladvies_naar'],
				schooljaarFilter: 'x_onderwijsresultaten_ds_schooljaar',
				description:
					'Toont per groep de gemiddelde score die aangeeft hoe de onderwijspositie van leerlingen in leerjaar 3 zich verhoudt tot het basisschooladvies. Een positieve score betekent dat leerlingen gemiddeld hoger zitten dan het advies; een negatieve score dat ze lager zitten.',
			},
			{
				path: 'onderbouwsnelheid',
				name: 'Onderbouwsnelheid',
				title: 'Onderbouwsnelheid %prognose%',
				vo: true,
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
				fixedWeergaveOpties: ['variant'],
				flexibleWeergaveOpties: ['indicator-over', 'or-uitsluiten', 'brin-overgang'],
				domain: 'fac_ds_doorstroom',
				maxGroeperingen: 0,
				schooljaarFilter: 'x_onderwijsresultaten_ds_schooljaar',
				description:
					'Toont het percentage leerlingen dat vanuit leerjaar 1 en 2 zonder vertraging doorstroomt naar het volgende leerjaar, berekend als gewogen gemiddelde over meerdere schooljaren conform de definitie van de onderwijsinspectie.',
			},
			{
				path: 'bovenbouwsucces',
				name: 'Bovenbouwsucces',
				title: 'Bovenbouwsucces %prognose%',
				vo: true,
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
				fixedWeergaveOpties: ['variant'],
				flexibleWeergaveOpties: ['indicator-over', 'or-uitsluiten', 'brin-overgang'],
				domain: 'fac_ds_doorstroom',
				maxGroeperingen: 0,
				schooljaarFilter: 'x_onderwijsresultaten_ds_schooljaar',
				description:
					'Toont het percentage leerlingen dat een bovenbouwleerjaar succesvol afrondt door over te gaan, te slagen of op te stromen, berekend als gewogen gemiddelde over meerdere schooljaren conform de definitie van de onderwijsinspectie.',
			},
			{
				path: 'examencijfers',
				name: 'Examencijfers',
				vo: true,
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
				fixedWeergaveOpties: ['variant'],
				flexibleWeergaveOpties: ['indicator-over', 'or-uitsluiten'],
				defaultGroeperingen: [['cf_fk_vk', 'vk_nm_kernvak']],
				domain: 'fac_cf_cijfer',
				maxGroeperingen: 1,
				toegestaneGroeperingen: [
					'cf_fk_vk.vk_nm_kernvak',
					'cf_nm_vak',
					'cf_fk_vk.vk_nm_vak',
					'cf_nm_lesgroep_docenten',
					'cf_fks_mw.mw_nm_medewerker',
				],
				schooljaarFilter: 'x_onderwijsresultaten_cf_schooljaar',
				description:
					'Toont het gemiddeld behaalde cijfer voor het centraal examen over alle vakken van geslaagde en gezakte leerlingen, berekend als gewogen gemiddelde over meerdere schooljaren conform de definitie van de onderwijsinspectie.',
			},
			{
				path: 'verschil-se-ce',
				name: 'Verschil SE-CE',
				vo: true,
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
				fixedWeergaveOpties: ['variant'],
				flexibleWeergaveOpties: ['indicator-over', 'or-uitsluiten'],
				defaultGroeperingen: [['cf_fk_vk', 'vk_nm_kernvak']],
				domain: 'fac_cf_cijfer',
				customFilters: [CIJFER_METZONDER_CE_CUSTOM_FILTER],
				maxGroeperingen: 1,
				toegestaneGroeperingen: [
					'cf_fk_vk.vk_nm_kernvak',
					'cf_nm_vak',
					'cf_fk_vk.vk_nm_vak',
					'cf_nm_lesgroep_docenten',
					'cf_fks_mw.mw_nm_medewerker',
				],
				schooljaarFilter: 'x_onderwijsresultaten_cf_schooljaar',
				description:
					'Toont het gemiddelde verschil tussen het cijfer voor het schoolexamen (SE) en het cijfer voor het centraal examen (CE), berekend over alle examenvakken van geslaagde en gezakte leerlingen als gewogen gemiddelde over meerdere schooljaren.',
			},
		],
	},
	{
		path: 'prestatieanalyse',
		name: 'Prestatieanalyse',
		base: 'core',
		children: [
			{
				path: 'prestatieanalyse',
				name: 'Prestatieanalyse',
				vso: true,
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
				fixedWeergaveOpties: ['variant'],
				domain: 'fac_pv_prestatieanalyse_vso',
				description:
					'Toont de vier VSO-indicatoren (Behaald niveau conform OPP, Beoogd i.r.t. behaald einduitstroomniveau, Tussentijdse uitstroom, Einduitstroom) per vestiging, vergeleken met vergelijkbare VSO-scholen binnen hetzelfde cluster van de onderwijsinspectie.',
			},
			{
				path: 'behaald-niveau-conform-opp',
				name: 'Behaald niveau conform OPP',
				vso: true,
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
				fixedWeergaveOpties: ['variant'],
				flexibleWeergaveOpties: ['uitstroomniveauvso'],
				defaultGroeperingen: [['ds_fk_ilt_van', 'ilt_nm_niveau'], ['ds_nr_leerjaar_van']],
				domain: 'fac_ds_doorstroom',
				subgroepen: ['ds_fun_nm_vergelijk_einduitstroomniveau_vso'],
				schooljaarFilter: 'x_prestatieanalyse_ds_schooljaar',
				description:
					'Toont het percentage VSO-leerlingen dat einduitstroomt op het niveau van het laatste ontwikkelingsperspectief (OPP), en het percentage dat boven of onder dit niveau uitstroomt. Alleen leerlingen met een diploma die minimaal 6 weken ingeschreven waren tellen mee.',
			},
			{
				path: 'beoogd-irt-behaald',
				name: 'Beoogd i.r.t. behaald einduitstroomniveau',
				vso: true,
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
				fixedWeergaveOpties: ['variant'],
				flexibleWeergaveOpties: ['verschilbeoogd'],
				defaultGroeperingen: [['ds_fk_ilt_van', 'ilt_nm_niveau'], ['ds_nr_leerjaar_van']],
				domain: 'fac_ds_doorstroom',
				schooljaarFilter: 'x_prestatieanalyse_ds_schooljaar',
				description:
					'Toont het gemiddelde absolute verschil (in uitstroompunten) tussen het beoogde einduitstroomniveau uit het laatste OPP en het werkelijk behaalde einduitstroomniveau van VSO-leerlingen. Uitstroom boven en onder het beoogde niveau wegen even zwaar.',
			},
			{
				path: 'tussentijdse-uitstroom',
				name: 'Tussentijdse uitstroom',
				vso: true,
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
				fixedWeergaveOpties: ['variant'],
				flexibleWeergaveOpties: ['tussentijdseuitstroomvso'],
				defaultGroeperingen: [['ds_fk_ilt_van', 'ilt_nm_niveau'], ['ds_nr_leerjaar_van']],
				domain: 'fac_ds_doorstroom',
				subgroepen: ['ds_nm_tussentijdse_uitstroom_vso'],
				schooljaarFilter: 'x_prestatieanalyse_ds_schooljaar',
				description:
					'Toont de verhouding tussen VSO-leerlingen die tussentijds (zonder diploma) uitstromen richting regulier onderwijs versus speciaal onderwijs. Leerlingen die uitstromen naar arbeid of minder dan 6 weken ingeschreven waren tellen niet mee.',
			},
			{
				path: 'einduitstroom',
				name: 'Einduitstroom',
				vso: true,
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
				fixedWeergaveOpties: ['variant'],
				flexibleWeergaveOpties: ['categorieuitstroomniveauvso'],
				defaultGroeperingen: [['ds_fk_ilt_van', 'ilt_nm_niveau'], ['ds_nr_leerjaar_van']],
				domain: 'fac_ds_doorstroom',
				subgroepen: ['ds_fun_nm_categorie_uitstroomniveau_vso'],
				schooljaarFilter: 'x_prestatieanalyse_ds_schooljaar',
				description:
					'Toont de verhouding tussen VSO-leerlingen die einduitstromen (met diploma) richting vervolgonderwijs versus arbeid. Alleen leerlingen die minimaal 6 weken ingeschreven waren tellen mee.',
			},
		],
	},
	{
		path: 'basisvaardigheden',
		name: 'Basisvaardigheden',
		base: 'core',
		children: [
			{
				path: 'leesvaardigheid',
				name: 'Leesvaardigheid',
				bv_nm_vaardigheid: 'Leesvaardigheid',
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR, RRol.SECTIELEIDER, RRol.DOCENT, RRol.MENTOR],
				fixedWeergaveOpties: ['aspect'],
				defaultGroeperingen: [['bv_fk_ilt', 'ilt_nm_niveau'], ['bv_nr_leerjaar'], ['bv_fk_vs', 'vs_nm_vestiging']],
				keepGroepering: true,
				domain: 'fac_bv_basisvaardigheden',
				subgroepen: ['bv_nm_toetsmoment'],
				description:
					"Toont de leesvaardigheidsscores van leerlingen uitgedrukt in referentieniveaus (0F t/m 4F). Via de weergave 'Gemiddelde' zie je het gemiddelde referentieniveau per groep; via 'Verdeling' zie je de toetsmomenten en het percentage leerlingen dat verbeterd is ten opzichte van het vorige toetsmoment.",
			},
			{
				path: 'taalverzorging',
				name: 'Taalverzorging',
				bv_nm_vaardigheid: 'Taalverzorging',
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR, RRol.SECTIELEIDER, RRol.DOCENT, RRol.MENTOR],
				fixedWeergaveOpties: ['aspect'],
				defaultGroeperingen: [['bv_fk_ilt', 'ilt_nm_niveau'], ['bv_nr_leerjaar'], ['bv_fk_vs', 'vs_nm_vestiging']],
				keepGroepering: true,
				domain: 'fac_bv_basisvaardigheden',
				subgroepen: ['bv_nm_toetsmoment'],
				description:
					"Toont de taalverzorgingsscores van leerlingen uitgedrukt in referentieniveaus (0F t/m 4F). Via de weergave 'Gemiddelde' zie je het gemiddelde referentieniveau per groep; via 'Verdeling' zie je de toetsmomenten en het percentage leerlingen dat verbeterd is ten opzichte van het vorige toetsmoment.",
			},
			{
				path: 'rekenen',
				name: 'Rekenen',
				bv_nm_vaardigheid: 'Rekenen',
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR, RRol.SECTIELEIDER, RRol.DOCENT, RRol.MENTOR],
				fixedWeergaveOpties: ['aspect'],
				defaultGroeperingen: [['bv_fk_ilt', 'ilt_nm_niveau'], ['bv_nr_leerjaar'], ['bv_fk_vs', 'vs_nm_vestiging']],
				keepGroepering: true,
				domain: 'fac_bv_basisvaardigheden',
				subgroepen: ['bv_nm_toetsmoment'],
				description:
					"Toont de rekenscores van leerlingen uitgedrukt in referentieniveaus (0F t/m 3F). Via de weergave 'Gemiddelde' zie je het gemiddelde referentieniveau per groep; via 'Verdeling' zie je de toetsmomenten en het percentage leerlingen dat verbeterd is ten opzichte van het vorige toetsmoment.",
			},
		],
	},
	{
		path: 'cijfers',
		name: 'Cijfers',
		base: 'core',
		children: [
			{
				path: 'overzicht',
				name: 'Cijferoverzicht',
				tab: 'Overzicht',
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR, RRol.SECTIELEIDER, RRol.DOCENT, RRol.MENTOR],
				fixedWeergaveOpties: ['variant'],
				flexibleWeergaveOpties: ['col'],
				defaultGroeperingen: [['cf_fk_ilt', 'ilt_nm_niveau'], ['cf_nr_leerjaar']],
				domain: 'fac_cf_cijfer',
				customFilters: [CIJFERTYPE_CUSTOM_FILTER, GEM_PERIODE_CUSTOM_FILTER],
				description:
					'Laat cijfers zien van leerlingen in verschillende groepen. Het dashboard toont het gemiddelde cijfer, het aantal (verschillende) leerlingen in de groep, het totaal aantal tekortpunten en het percentage onvoldoendes. Onderaan staat het gemiddelde van alle cijfers en het totaal aantal (verschillende) leerlingen met een cijfer.',
			},
			{
				path: 'docent',
				name: 'Docent',
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.SECTIELEIDER],
				fixedWeergaveOpties: ['variant'],
				domain: 'fac_cf_cijfer',
				customFilters: [CIJFERTYPE_CUSTOM_FILTER],
				description:
					'Toont een overzicht van alle docenten die cijfers hebben gegeven, met per docent het gemiddelde cijfer, het percentage onvoldoende en het aantal leerlingen, inclusief een staafdiagram met de verdeling van de cijfers. Cijfers worden aan docenten gekoppeld via lesgroepen.',
			},
			{
				path: 'toets',
				name: 'Toets',
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.SECTIELEIDER, RRol.DOCENT, RRol.MENTOR],
				fixedWeergaveOpties: [],
				flexibleWeergaveOpties: ['cijfertype', 'dossier'],
				defaultGroeperingen: [['cf_nm_vak']],
				domain: 'fac_cf_cijfer',
				subgroepen: ['cf_fun_nm_periode', 'cf_nm_kolomtype', 'cf_fun_nm_kolomgroep'],
				description:
					'Toont gemiddelde cijfers voor individuele toetsen per periode. Je kunt kiezen voor weergave van individuele toetsen, periodecijfers (R1–R4, SE, CE) of beide. Somtoday-gebruikers kunnen ook kiezen tussen het voortgangsdossier en het examendossier.',
			},
			{
				path: 'toets-details',
				name: 'Toets details',
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.SECTIELEIDER, RRol.DOCENT],
				fixedWeergaveOpties: [],
				flexibleWeergaveOpties: ['cijfertype', 'dossier'],
				domain: 'fac_cf_cijfer',
				description:
					'Toont cijfers per individuele toets met meer detail, gegroepeerd op lesgroep en vak, standaard gefilterd op één niveau en leerjaar. Je kunt kiezen uit toets, periodecijfer of beide weergave, en (voor Somtoday) uit voortgangs- of examendossier.',
			},
			{
				path: 'se-ce',
				name: 'SE-CE',
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.SECTIELEIDER, RRol.BESTUUR, RRol.DOCENT],
				fixedWeergaveOpties: ['variant'],
				flexibleWeergaveOpties: ['cijfersseceweergave'],
				defaultGroeperingen: [['cf_fk_ilt', 'ilt_nm_niveau'], ['cf_nm_vak']],
				domain: 'fac_cf_cijfer',
				customFilters: [CIJFER_METZONDER_CE_CUSTOM_FILTER],
				description:
					'Brengt de verschillen in beeld tussen de cijfers voor het schoolexamen (SE) en het centraal examen (CE) per vak en niveau, gevisualiseerd met een gekleurd staafdiagram. Standaard worden alleen vakken met een CE-resultaat getoond.',
			},
			{
				path: 'cijferlijst',
				name: 'Cijferlijst',
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.SECTIELEIDER, RRol.MENTOR],
				fixedWeergaveOpties: [],
				flexibleWeergaveOpties: ['tekortpunten', 'vakOrder'],
				defaultGroeperingen: [['cf_nm_klas']],
				domain: 'fac_cf_cijfer',
				customFilters: [
					{
						att: 'x_cijferlijst_cijfertype',
						label: 'Cijfertype',
						optiesPerBron: CIJFERLIJST_CIJFERTYPE_OPTIES_PER_BRON,
						default: 'Laatste gemiddelde',
						omschrijving: 'Welk soort cijfer de cijferlijst toont. Altijd gezet, standaard Laatste gemiddelde.',
					},
					{
						att: 'x_cijferlijst_gem_periode',
						label: 'Periode',
						omschrijving: PERIODE_OMSCHRIJVING,
					},
				],
				subgroepen: ['cf_abb_vak'],
				description:
					'Toont volledige cijferlijsten per leerling, geschikt voor gebruik bij rapportvergaderingen. Leerlingen kunnen worden gefilterd op het aantal tekortpunten en resultaten kunnen worden gesorteerd op tekortpunten, vaknummer of alfabetisch.',
			},
			{
				path: 'examens',
				name: 'Examens',
				title: 'Examens %prognose%',
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.SECTIELEIDER, RRol.BESTUUR, RRol.DOCENT],
				fixedWeergaveOpties: ['variant'],
				flexibleWeergaveOpties: ['afwijkingpercentiel'],
				defaultGroeperingen: [['ekc_nm_niveau'], ['ekc_fk_vk', 'vk_nm_vak']],
				domain: 'fac_ekc_examenkandidaten_en_cijfers',
				vasteGroeperingen: ['ekc_fk_br_vest.br_co_brin'],
				description:
					'Vergelijkt de eigen examenresultaten met het landelijk gemiddelde (gepubliceerd door DUO) per niveau, vakkengroep of vak. Zolang DUO nog geen definitieve uitslagen heeft gepubliceerd, toont CumLaude een prognose op basis van cijfers van andere aangesloten scholen.',
			},
		],
	},
	{
		path: 'doorstroom',
		name: 'Doorstroom',
		base: 'core',
		children: [
			{
				path: 'idu',
				name: 'IDU',
				top: true,
				children: [
					{
						path: 'doorstroom',
						name: 'Doorstroom',
						title: 'Doorstroom %prognose%',
						requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
						fixedWeergaveOpties: ['variant'],
						flexibleWeergaveOpties: ['doorstroomweergave', 'brin-overgang'],
						defaultGroeperingen: [['ds_fk_ilt_van', 'ilt_nm_niveau'], ['ds_nr_leerjaar_van']],
						domain: 'fac_ds_doorstroom',
						subgroepen: ['ds_nm_idu'],
						description:
							'Laat per groep de verdeling zien van succesvol/onsuccesvol of bevorderd/niet bevorderd met aantallen en percentages (percentage succesvol/bevorderd binnen de groep). Je ziet ook een onderverdeling naar niveau en leerjaar van bestemming. Onderaan het dashboard staat het totaal aantal leerlingen en het totale bevorderingspercentage.',
					},
					{
						path: 'matrix',
						name: 'Matrix',
						title: 'Matrix %prognose%',
						requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
						fixedWeergaveOpties: ['variant'],
						flexibleWeergaveOpties: ['brin-overgang', 'eenheid'],
						domain: 'fac_ds_doorstroom',
						maxGroeperingen: 0,
						description:
							'Toont de doorstroom van en naar alle leerfases in een overzichtelijke matrix. Links zie je de niveaus waarvandaan leerlingen doorstromen, onderaan de niveaus waarnaartoe zij gaan. In de matrix staan de aantallen leerlingen die van en naar het betreffende niveau doorstromen. Links en onder de matrix staan de totalen.',
					},
					{
						path: 'geslaagd',
						name: 'Geslaagd',
						title: 'Geslaagd %prognose%',
						requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
						fixedWeergaveOpties: ['variant'],
						defaultGroeperingen: [['ds_fk_ilt_van', 'ilt_nm_niveau'], ['ds_nr_leerjaar_van']],
						domain: 'fac_ds_doorstroom',
						subgroepen: ['ds_fun_nm_examenstatus_met_prognose'],
						description:
							'Geeft slagingspercentages weer. Het dashboard toont per groep de verdeling van geslaagde/afgewezen leerlingen met aantallen en slagingspercentages binnen de groep. Onderaan staat het totaal aantal leerlingen en het totale slagingspercentage. Het dashboard filtert standaard op leerlingen met een examenuitslag.',
					},
					{
						path: 'instroom',
						name: 'Instroom',
						title: 'Instroom %prognose%',
						requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
						fixedWeergaveOpties: ['variant'],
						flexibleWeergaveOpties: ['eenheid'],
						defaultGroeperingen: [['ds_fk_ll', 'll_nm_svh'], ['ds_co_brin_svh']],
						domain: 'fac_ds_doorstroom',
						description:
							'Laat zien hoeveel leerlingen per gekozen groepering in het gekozen schooljaar zijn ingestroomd en aan het eind van het jaar nog steeds ingeschreven zijn. Met het Instroomtype-filter kun je kiezen tussen externe instroom (vanuit basisonderwijs of andere instelling) en/of interne instroom (van een andere vestiging). Standaard zie je alleen externe instroom. Naast leerlingaantallen zijn ook percentages van de totale (externe of interne) instroom zichtbaar.',
					},
					{
						path: 'uitstroom',
						name: 'Uitstroom',
						title: 'Uitstroom %prognose%',
						requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
						fixedWeergaveOpties: ['variant'],
						flexibleWeergaveOpties: ['eenheid'],
						defaultGroeperingen: [['ds_fk_ll', 'll_nm_svb']],
						domain: 'fac_ds_doorstroom',
						description:
							'Laat zien hoeveel leerlingen per gekozen groepering in het gekozen schooljaar op 1 oktober waren ingeschreven en gedurende of aan het eind van het schooljaar zijn uitgestroomd. Met het Uitstroomtype-filter kun je kiezen tussen externe uitstroom (verlaat de instelling) en/of interne uitstroom (naar een andere vestiging). Standaard zie je alleen externe uitstroom. Naast leerlingaantallen zijn ook percentages van de totale (externe of interne) uitstroom zichtbaar.',
					},
					{
						path: 'overgang',
						name: 'Overgang',
						title: 'Overgang %prognose%',
						requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
						fixedWeergaveOpties: ['variant'],
						flexibleWeergaveOpties: ['eenheid'],
						defaultGroeperingen: [['ds_fk_ilt_van', 'ilt_nm_niveau']],
						domain: 'fac_ds_doorstroom',
						customFilters: [
							{
								att: 'x_met_overgang',
								label: 'Overgang',
								opties: [1, 0],
								omschrijving:
									'`1` toont alleen leerlingen mét een overgang (ander groepeerveld naar dan van), `0` alleen leerlingen zonder overgang. Zonder dit filter, of met beide, zijn alle leerlingen te zien.',
							},
						],
						maxGroeperingen: 1,
						groeperingRegel:
							'Groeperen kan alleen op een veld dat zowel een variant voor dit als voor volgend schooljaar heeft, zoals Niveau, Klas, Profiel of Vestiging.',
						description:
							'Toont leerlingen die een andere plaatsing hebben gekregen, onderverdeeld op overgangsmoment (einde schooljaar of tussentijds) en overgangstype (met of zonder overgang van klas, niveau, sector/profiel of vestiging).',
					},
				],
			},
			{
				path: 'advies',
				name: 'Advies',
				top: true,
				children: [
					{
						path: 'plaatsing',
						name: 'Plaatsing / Advies',
						title: 'Plaatsing / Advies %prognose%',
						requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
						fixedWeergaveOpties: ['variant'],
						flexibleWeergaveOpties: ['threshold'],
						defaultGroeperingen: [['ds_fk_ilt_van', 'ilt_nm_niveau'], ['ds_nr_leerjaar_van']],
						domain: 'fac_ds_doorstroom',
						customFilters: [
							PEILDATUM_CUSTOM_FILTER,
							{
								att: 'x_advies_leerlinggroep',
								label: 'Leerlinggroep',
								opties: ADVIES_LEERLINGGROEP_OPTIES,
								omschrijving:
									'Beperkt het dashboard tot instromers (extern ingestroomd in leerjaar 1) of tot examenkandidaten. Zonder dit filter zijn alle leerlingen te zien.',
							},
						],
						subgroepen: ['ds_fun_co_verschil_basisschooladvies_van'],
						description:
							'Toont het adviesverschil: het verschil tussen de onderwijspositie van leerlingen in leerjaar 3 en het basisschooladvies, berekend conform de definitie van de onderwijsinspectie en uitgedrukt als percentage. Een positieve score betekent dat leerlingen gemiddeld hoger zitten dan het advies.',
					},
				],
			},
			{
				path: 'cohort',
				name: 'Cohortrendement',
				top: true,
				children: [
					{
						path: 'rendement',
						name: 'Cohortrendement',
						title: 'Cohortrendement %prognose%',
						requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
						fixedWeergaveOpties: ['variant'],
						flexibleWeergaveOpties: ['cohortrendementtype', 'cohortrendementweergave'],
						defaultGroeperingen: [['ds_fk_ilt_van', 'ilt_nm_niveau'], ['ds_nr_leerjaar_van']],
						domain: 'fac_ds_doorstroom',
						description:
							'Laat zien hoe geselecteerde cohorten hebben gepresteerd over hun gehele onderwijsloopbaan. Je kunt kiezen uit vier typen (cohortrendement tot/vanaf, examenrendement, opstroom rendement) en drie weergaven (doublures, niveauverschil, cohortrendement).',
					},
					{
						path: 'details',
						name: 'Cohortdetails',
						requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
						fixedWeergaveOpties: [],
						flexibleWeergaveOpties: ['cohortrendementtype', 'eenheid'],
						unsupportedOptions: ['sortOrder'],
						domain: 'fac_ds_doorstroom',
						description:
							'Geeft voor een geselecteerd cohort per leerfase een gedetailleerd stroomdiagram van de doorstroom van leerlingen, inclusief externe in- en uitstroom. Je bereikt dit dashboard door vanuit Cohortrendement door te klikken op een specifiek cohort.',
					},
				],
			},
		],
	},
	{
		path: 'aanwezigheid',
		name: 'Aanwezigheid',
		base: 'core',
		children: [
			{
				path: 'overzicht',
				name: 'Aanwezigheid overzicht',
				tab: 'Overzicht',
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.SECTIELEIDER, RRol.MENTOR, RRol.AANWEZIGHEID, RRol.BESTUUR],
				requiredModule: RCumLaudeModule.SCHOOLAANWEZIGHEID,
				fixedWeergaveOpties: [],
				flexibleWeergaveOpties: ['adhoc', 'from', 'aanwezigheiduitsplitsing'],
				unsupportedOptions: ['sortOrder'],
				domain: 'fac_aw_aanwezigheid',
				maxGroeperingen: 0,
				subgroepen: ['aw_fun_nm_interventielaag'],
				description:
					'Toont de aanwezigheid van leerlingen per maand in drie interventielagen op basis van het MD-MTSS Piramidemodel: goede aanwezigheid (≥95%), beginnend verzuim (90–95%) en aanhoudend verzuim (≤90%). Je ziet een cumulatieve piramide, een piramide per maand en een overzicht per leerling, klas of vak.',
			},
			{
				path: 'statistieken',
				name: 'Aanwezigheid statistieken',
				tab: 'Statistieken',
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.SECTIELEIDER, RRol.MENTOR, RRol.AANWEZIGHEID, RRol.BESTUUR],
				requiredModule: RCumLaudeModule.SCHOOLAANWEZIGHEID,
				fixedWeergaveOpties: [],
				flexibleWeergaveOpties: ['reden-maand', 'lesuur-maand', 'adhoc', 'from'],
				domain: 'fac_aw_aanwezigheid',
				description:
					'Toont de aanwezigheidsstatistieken van een geselecteerde groep leerlingen, inclusief het totaal aantal geregistreerde leerlingen en de bijbehorende afwezigheidsredenen van de laatste afgeronde maand.',
			},
			{
				path: 'datavolledigheid',
				name: 'Aanwezigheid datavolledigheid',
				tab: 'Datavolledigheid',
				title: 'Datavolledigheid',
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.SECTIELEIDER, RRol.MENTOR, RRol.AANWEZIGHEID],
				requiredModule: RCumLaudeModule.SCHOOLAANWEZIGHEID,
				fixedWeergaveOpties: [],
				flexibleWeergaveOpties: [],
				defaultGroeperingen: [['aw_fun_nm_vak_afspraak']],
				// Op Datavolledigheid zijn leerling-varianten van diverse velden vervangen door afspraak-varianten:
				// het gaat niet om lesuren waarop een leerling o.a. Duits had, maar specifiek lessen Duits.
				attOverrides: {
					aw_fun_nm_vak: NIET_BESCHIKBAAR,
					aw_fun_abb_vak: NIET_BESCHIKBAAR,
					aw_fun_nm_afspraaktype: NIET_BESCHIKBAAR,
					aw_is_presentieregistratie_verwerkt: NIET_BESCHIKBAAR,
					aw_is_presentieregistratie_verplicht: NIET_BESCHIKBAAR,
					aw_fks_mw: NIET_BESCHIKBAAR,
					aw_fks_lg: NIET_BESCHIKBAAR,
					'aw_fks_af.af_is_presentieregistratie_verplicht': NIET_BESCHIKBAAR,

					aw_fun_nm_vak_afspraak: BESCHIKBAAR,
					aw_fun_abb_vak_afspraak: BESCHIKBAAR,
					aw_fun_nm_medewerker_afspraak: BESCHIKBAAR,
					aw_fun_abb_medewerker_afspraak: BESCHIKBAAR,
					aw_fun_nm_lesgroep_afspraak: BESCHIKBAAR,
					aw_fun_is_presentieregistratie_verplicht_afspraak: BESCHIKBAAR,
					// Geldt voor alle velden die aw_fks_af ontsluit; die horen per definitie bij de les.
					aw_fks_af: BESCHIKBAAR,
				},
				domain: 'fac_aw_aanwezigheid',
				description:
					'Toont per vak het aantal lessen, het aantal afgesloten en niet-afgesloten lesuren en het percentage afgesloten lesuren. Hiermee kun je controleren of de presentieregistratie volledig is bijgehouden door docenten. Bij Somtoday worden afspraken waarvoor geen presentieregistratie verplicht is standaard buiten beschouwing gelaten.',
			},
			{
				path: 'afwezigheid',
				name: 'Afwezigheid',
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.SECTIELEIDER, RRol.MENTOR, RRol.AANWEZIGHEID, RRol.BESTUUR],
				fixedWeergaveOpties: ['variant'],
				flexibleWeergaveOpties: ['tijdseenheid', 'interval', 'adhoc', 'from'],
				defaultGroeperingen: [['aw_fk_ilt', 'ilt_nm_niveau'], ['aw_nr_leerjaar']],
				domain: 'fac_aw_aanwezigheid',
				customFilters: [
					{
						att: 'x_abs_geoorloofd',
						label: 'Absentie',
						opties: ABSENTIE_LIJNEN_OPTIES,
						omschrijving: 'Geen echt filter maar een weergaveoptie: welke lijnen in de grafiek zichtbaar zijn. Standaard alle drie.',
						variant: 'historie',
					},
				],
				description:
					'Toont geoorloofde en ongeoorloofde absenties van leerlingen als waarnemingen (alleen lesuren waarvoor leerlingen daadwerkelijk ingeroosterd stonden). De weergave is instelbaar in lesuren, lestijd, lesdagen of percentage.',
			},
			{
				path: 'lesregistraties',
				name: 'Lesregistraties',
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.SECTIELEIDER, RRol.MENTOR, RRol.AANWEZIGHEID, RRol.BESTUUR],
				fixedWeergaveOpties: ['variant'],
				flexibleWeergaveOpties: ['interval', 'adhoc', 'from'],
				defaultGroeperingen: [['lr_fk_ilt', 'ilt_nm_niveau'], ['lr_nr_leerjaar']],
				domain: 'fac_lr_lesregistratie',
				description:
					'Toont lesregistraties per waarneming, zoals te laat komen, verwijdering, huiswerk niet in orde en materiaal niet in orde. Bij één waarneming kunnen meerdere registraties zijn. Somtoday-scholen kunnen ook eigen registratiecategorieën aanmaken.',
			},
		],
	},
	{
		path: 'leerling',
		name: 'Leerling',
		base: 'core',
		children: [
			{
				path: 'leerling',
				name: 'Leerling',
				title: 'Leerling %prognose%',
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
				fixedWeergaveOpties: ['variant'],
				flexibleWeergaveOpties: ['eenheid'],
				defaultGroeperingen: [['ds_fk_ilt_van', 'ilt_nm_niveau'], ['ds_nr_leerjaar_van']],
				domain: 'fac_ds_doorstroom',
				customFilters: [PEILDATUM_CUSTOM_FILTER],
				description:
					'Laat aantallen zien van leerlingen in verschillende groepen en percentages ten opzichte van het totaal. Onderaan het dashboard staat het totaal aantal leerlingen.',
			},
			{
				path: 'kenmerk',
				name: 'Kenmerk',
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
				fixedWeergaveOpties: ['variant'],
				flexibleWeergaveOpties: ['eenheid', 'kenmerk'],
				defaultGroeperingen: [['ds_fk_ilt_van', 'ilt_nm_niveau'], ['ds_nr_leerjaar_van']],
				domain: 'fac_ds_doorstroom',
				customFilters: [PEILDATUM_CUSTOM_FILTER],
				description:
					'Laat per leerlingkenmerk (APCG, VAVO, LWOO, instroom, uitstroom, examenopstroom) zien hoeveel leerlingen hieraan voldoen en welk percentage van het totaal dit is.',
			},
			{
				path: 'vakken',
				name: 'Vakken',
				requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
				fixedWeergaveOpties: ['variant'],
				flexibleWeergaveOpties: ['eenheid'],
				defaultGroeperingen: [['vkk_fk_ilt', 'ilt_nm_niveau'], ['vkk_nr_leerjaar']],
				domain: 'fac_vkk_vakkeuze',
				customFilters: [
					{
						att: 'x_vakkeuze_peildatum',
						label: 'Peildatum',
						opties: PEILDATUM_OPTIES,
						default: '1 oktober',
						omschrijving:
							'Welke peildatum de vakkeuze geldt. Zonder dit filter geldt 1 oktober, ook als de gebruiker niets noemt. Zet `Actueel` zodra de gebruiker de huidige of actuele vakkeuze wil.',
					},
				],
				description:
					'Laat de vakkeuzes van de leerlingen binnen de gekozen groepering zien. Naast je eigen groepering is dit dashboard ook altijd gegroepeerd op vak. Je ziet het aantal en het percentage leerlingen binnen de groep dat het betreffende vak heeft gekozen.',
			},
		],
	},
	{
		path: 'details',
		name: 'Details',
		base: 'core',
		children: [
			{
				path: 'leerling',
				name: 'Leerling',
				top: true,
				eigenTabbalk: true,
				children: [
					{
						path: 'doorstroom',
						name: 'Doorstroom',
						requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.MENTOR],
						fixedWeergaveOpties: ['selectie', 'adhoc', 'details-list', 'schooljaar', 'from'],
					},
					{
						path: 'basisvaardigheden',
						name: 'Basisvaardigheden',
						children: [
							{
								path: 'leesvaardigheid',
								name: 'Leesvaardigheid',
								bv_nm_vaardigheid: 'Leesvaardigheid',
								requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.SECTIELEIDER, RRol.DOCENT, RRol.MENTOR],
								fixedWeergaveOpties: ['selectie', 'adhoc', 'details-list', 'schooljaar', 'from'],
							},
							{
								path: 'taalverzorging',
								name: 'Taalverzorging',
								bv_nm_vaardigheid: 'Taalverzorging',
								requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.SECTIELEIDER, RRol.DOCENT, RRol.MENTOR],
								fixedWeergaveOpties: ['selectie', 'adhoc', 'details-list', 'schooljaar', 'from'],
							},
							{
								path: 'rekenen',
								name: 'Rekenen',
								bv_nm_vaardigheid: 'Rekenen',
								requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.SECTIELEIDER, RRol.DOCENT, RRol.MENTOR],
								fixedWeergaveOpties: ['selectie', 'adhoc', 'details-list', 'schooljaar', 'from'],
							},
						],
					},
					{
						path: 'cijferlijst',
						name: 'Cijferlijst',
						requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.SECTIELEIDER, RRol.DOCENT, RRol.MENTOR],
						fixedWeergaveOpties: ['selectie', 'adhoc', 'details-list', 'schooljaar', 'from', 'variant'],
						flexibleWeergaveOpties: ['cijfertype', 'dossier'],
						defaultGroeperingen: [['cf_nm_vak'], ['cf_nm_leerling']],
					},
					{
						path: 'aanwezigheid',
						name: 'Aanwezigheid',
						requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.SECTIELEIDER, RRol.MENTOR, RRol.AANWEZIGHEID],
						requiredModule: RCumLaudeModule.SCHOOLAANWEZIGHEID,
						fixedWeergaveOpties: ['selectie', 'adhoc', 'details-list', 'schooljaar', 'from'],
						flexibleWeergaveOpties: ['reden-maand', 'lesuur-maand'],
					},
					{
						path: 'afwezigheid',
						name: 'Afwezigheid',
						requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.SECTIELEIDER, RRol.MENTOR, RRol.AANWEZIGHEID],
						fixedWeergaveOpties: ['selectie', 'adhoc', 'details-list', 'schooljaar', 'from'],
					},
					{
						path: 'afwezigheidsredenen',
						name: 'Afwezigheidsredenen',
						requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.SECTIELEIDER, RRol.MENTOR, RRol.AANWEZIGHEID],
						fixedWeergaveOpties: ['selectie', 'adhoc', 'details-list', 'schooljaar', 'from'],
					},
					{
						path: 'lesregistraties',
						name: 'Lesregistraties',
						requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.SECTIELEIDER, RRol.MENTOR, RRol.AANWEZIGHEID],
						fixedWeergaveOpties: ['selectie', 'adhoc', 'details-list', 'schooljaar', 'from'],
					},
					{
						path: 'lesregistratieredenen',
						name: 'Lesregistratieredenen',
						requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.SECTIELEIDER, RRol.MENTOR, RRol.AANWEZIGHEID],
						fixedWeergaveOpties: ['selectie', 'adhoc', 'details-list', 'schooljaar', 'from'],
					},
					{
						path: 'vakken',
						name: 'Vakken',
						requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.MENTOR],
						fixedWeergaveOpties: ['selectie', 'adhoc', 'details-list', 'schooljaar', 'from'],
					},
				],
			},
			{
				path: 'docent',
				name: 'Docent',
				top: true,
				eigenTabbalk: true,
				children: [
					{
						path: 'cijfers',
						name: 'Cijfers',
						requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.SECTIELEIDER],
						fixedWeergaveOpties: ['selectie', 'adhoc-docent', 'schooljaar', 'from', 'variant'],
					},
					{
						path: 'toets',
						name: 'Toets',
						requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.SECTIELEIDER, RRol.DOCENT],
						fixedWeergaveOpties: ['selectie', 'adhoc-docent', 'schooljaar', 'from'],
						flexibleWeergaveOpties: ['cijfertype', 'dossier'],
						defaultGroeperingen: [['cf_nm_vak']],
					},
				],
			},
			{
				path: 'uitzondering',
				name: 'Uitzonderingen',
				top: true,
				children: [
					{
						path: 'doorstroom',
						name: 'Doorstroom',
						requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
						fixedWeergaveOpties: ['from'],
					},
					{
						path: 'onderbouwsnelheid',
						name: 'Onderbouwsnelheid',
						vo: true,
						requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
						fixedWeergaveOpties: ['from'],
						flexibleWeergaveOpties: ['indicator-over'],
					},
					{
						path: 'onderwijspositie',
						name: 'Onderwijspositie',
						vo: true,
						requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
						fixedWeergaveOpties: ['from'],
						flexibleWeergaveOpties: ['indicator-over'],
					},
					{
						path: 'bovenbouwsucces',
						name: 'Bovenbouwsucces',
						vo: true,
						requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
						fixedWeergaveOpties: ['from'],
						flexibleWeergaveOpties: ['indicator-over'],
					},
					{
						path: 'uitstroomniveau',
						name: 'Uitstroomniveau',
						vso: true,
						requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
						fixedWeergaveOpties: ['from'],
					},
					{
						path: 'tussentijdse-uitstroom',
						name: 'Tussentijdse uitstroom',
						vso: true,
						requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
						fixedWeergaveOpties: ['from'],
					},
					{
						path: 'einduitstroom',
						name: 'Einduitstroom',
						vso: true,
						requiredRollen: [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.BESTUUR],
						fixedWeergaveOpties: ['from'],
					},
				],
			},
		],
	},
] as const satisfies ReadonlyArray<RegistryNode>;

/**
 * Zoekt een pad (bijv. '/doorstroom/onderbouwsnelheid') op als bladknoop in de registry. Geeft
 * undefined voor alles wat geen dashboard is (/start, /beheer/..., /shared/..., of een pad dat op
 * een tussenknoop uitkomt).
 */
export function findDashboardLeaf(pad: string): { hoofdpagina: string; naam: string; titel: string } | undefined {
	const padSegmenten = pad.replace(/^\//, '').split('/');
	return zoekBladInBoom(DASHBOARD_REGISTRY, padSegmenten, undefined, []);
}

function zoekBladInBoom(
	nodes: ReadonlyArray<RegistryNode>,
	padSegmenten: string[],
	hoofdpagina: string | undefined,
	titelSegmenten: string[]
): { hoofdpagina: string; naam: string; titel: string } | undefined {
	const [padSegment, ...restPadSegmenten] = padSegmenten;
	const node = nodes.find((n) => n.path === padSegment);
	if (!node) return undefined;

	const top = hoofdpagina ?? node.name ?? '';
	const nieuweTitelSegmenten = [...titelSegmenten, node.tab ?? node.name ?? ''];

	if (node.children) {
		if (restPadSegmenten.length === 0) return undefined;
		return zoekBladInBoom(node.children, restPadSegmenten, top, nieuweTitelSegmenten);
	}

	if (restPadSegmenten.length > 0) return undefined;
	return { hoofdpagina: top, naam: node.name ?? '', titel: nieuweTitelSegmenten.join(' - ') };
}

function collectAttOverrides(nodes: ReadonlyArray<RegistryNode>, acc: AttOverrides[]): AttOverrides[] {
	for (const node of nodes) {
		if (node.attOverrides) acc.push(node.attOverrides);
		if (node.children) collectAttOverrides(node.children, acc);
	}
	return acc;
}

const ALLE_ATT_OVERRIDES = collectAttOverrides(DASHBOARD_REGISTRY, []);

/**
 * De attribuut-overrides van alle dashboards, elk apart. Bedoeld voor code die moet weten wat er
 * ergens in de applicatie kan voorkomen; wie de overrides van één dashboard nodig heeft leest ze uit
 * de route-data.
 */
export function allAttOverrides(): ReadonlyArray<AttOverrides> {
	return ALLE_ATT_OVERRIDES;
}

/**
 * De `details`-tak telt niet mee: die namen overlappen met die van hoofddashboards.
 */
export function findDashboardOptionsByName(name: string): readonly DashboardOptionDefinition[] {
	const zoek = (nodes: ReadonlyArray<RegistryNode>): readonly DashboardOptionDefinition[] | undefined => {
		for (const node of nodes) {
			if (node.children) {
				const gevonden = zoek(node.children);
				if (gevonden) return gevonden;
			} else if (node.name === name) {
				return dashboardOptionsFor(node);
			}
		}
		return undefined;
	};
	return zoek(DASHBOARD_REGISTRY.filter((n) => n.path !== 'details')) ?? [];
}
